import { v4 as uuidv4 } from 'uuid';
import { Field, FieldContext } from '../field';
import { FieldDefinition } from '../types/FieldDefinition';
import { getAllChildren } from '../utils/helpers';
import { extractIds } from '../../formulas/utils/helpers';

/**
 * A repeatable instance includes all of the original
 * children of this parent field, not the parent itself.
 */
interface RepeatableInstance {
  id: string;
  fields: Field[]
}

export class RepeatableField extends Field {
  private baseInstance?: RepeatableInstance;

  constructor({
    fieldDef, context,
  }: {
    fieldDef: FieldDefinition,
    context: FieldContext
  }) {
    super({
      fieldDef, context,
    });

    // We need a set timeout so all the child fields are initialised
    setTimeout(() => {
      this.baseInstance = {
        id: uuidv4(),
        fields: getAllChildren(this, this.context.initialisedFields),
      };

      this.setExistingContentForAllFields();
    }, 0);
  }

  /**
   * Add a repeatable instance
   */
  addInstance() {
    const rowId = uuidv4();
    if (!this.baseInstance) {
      return;
    }
    // eslint-disable-next-line consistent-return
    return this.generateFields(this.baseInstance.fields, rowId);
  }

  /**
   * Remove a repeatable instance
   */
  removeInstance(rowId: string) {
    const fieldsToDelete = this.context.initialisedFields
      .filter((field) => field.fieldDef.rowId === rowId);

    fieldsToDelete.forEach((field) => this.context.deleteField(field));
  }

  /**
   * Given a set of fields, copy them and give the new ids
   * but keep references to the originals
   * @param fields
   */
  generateFields(fields: Field[], rowId: string) {
    // Used to store a map between the fields' original id and
    // there new one
    const idMap = fields
      .reduce((idMapObject, field) => {
        // eslint-disable-next-line no-param-reassign
        idMapObject[field.id] = uuidv4();
        return idMapObject;
      }, {});

    // Hold the new fields
    const newFieldsDefs: FieldDefinition[] = fields
      .map((field) => RepeatableField.generateFieldDef(field, idMap, rowId))
      .map((field) => RepeatableField.updateIds(field, idMap));

    const newFields = newFieldsDefs.map((fieldDef) => this.context.createField(fieldDef));

    return { fields: newFields, idMap };
  }

  /**
   * Given a field create a new copy of it with new ids, referencing the old one
   * @param field The original field to generate from
   * @param idMap A map of old ids to new ids
   */
  static generateFieldDef(field: Field, idMap: { [key: string]: string }, rowId: string) {
    const newFieldDef: FieldDefinition = JSON.parse(JSON.stringify(field.fieldDef));
    newFieldDef.originalId = field.id;
    newFieldDef.id = idMap[field.id];
    newFieldDef.rowId = rowId;
    return newFieldDef;
  }

  /**
   * Update any id references with the new ids
   * @param field The field to have its ids updated
   * @param idMap A map of old ids to new ids
   */
  static updateIds(fieldDef: FieldDefinition, idMap) {
    const updatedFieldDef = JSON.parse(JSON.stringify(fieldDef));

    // Parent id
    if (fieldDef.parent && fieldDef.parent[0]) {
      updatedFieldDef.parent[0] = idMap[fieldDef.parent[0]]
        ? idMap[fieldDef.parent[0]]
        : updatedFieldDef.parent[0];
    }

    // Children ids
    if (fieldDef.children) {
      updatedFieldDef.children = fieldDef.children.map((childId) => idMap[childId]);
    }

    // formula ids
    if (fieldDef.formula) {
      const ids = extractIds({ formula: fieldDef.formula });
      ids.forEach((id) => {
        // Replace the formula id with a new one. Sometime there may not be a new id, this happens
        // when the formula references a global and in this case we should keep the original id.
        if (idMap[id]) {
          updatedFieldDef.formula = updatedFieldDef.formula.replace(id, idMap[id]);
        }
      });
    }

    // In version formulas is an array
    if (fieldDef.formulas) {
      fieldDef.formulas.forEach((formula, index) => {
        const ids = extractIds({ formula });
        ids.forEach((id) => {
          // Replace the formula id with a new one. Sometime there may not be a new id, this happens
          // when the formula references a global and in this case we should keep the original id.
          if (idMap[id]) {
            updatedFieldDef.formulas[index] = updatedFieldDef.formulas[index]
              .replace(id, idMap[id]);
          }
        });
      });
    }

    // Validation formulas
    if (fieldDef.validation && fieldDef.validation.length) {
      const computedValidation = updatedFieldDef.validation.filter(
        (validation) => validation.formula,
      );
      computedValidation.forEach((validation) => {
        const ids = extractIds(validation.formula);
        ids.forEach((id) => {
          if (idMap[id]) {
            // eslint-disable-next-line no-param-reassign
            validation.formula = validation.formula.replace(id, idMap[id]);
          }
        });
      });
    }

    return updatedFieldDef;
  }

  /**
   * This will set existing content for the repeatbale fields and will handle adding
   * additional rows if needed
   */
  private setExistingContentForAllFields() {
    if (!this.context.existingSubmission) {
      return;
    }

    // eslint-disable-next-line no-unused-expressions
    this.baseInstance?.fields.forEach((field) => {
      const existingContent = this.context.existingSubmission!.content[field.id];
      if (!existingContent) {
        return;
      }

      let fieldInstances = this.context.initialisedFields
        .filter((f) => f.id === field.id || f.fieldDef.originalId === field.id);

      // If its an array we need to enter the content for each item
      if (Array.isArray(existingContent)) {
        // If there arent as many instances as values in the array we need to add a row
        while (fieldInstances.length < existingContent.length) {
          this.addInstance();
          fieldInstances = this.context.initialisedFields
            .filter((f) => f.id === field.id || f.fieldDef.originalId === field.id);
        }

        existingContent.forEach((value, index) => {
          fieldInstances[index].setExistingContent(value);
        });
      } else {
        // If there isn't an instance yet add one
        if (fieldInstances.length < 1) {
          this.addInstance();
          fieldInstances = this.context.initialisedFields
            .filter((f) => f.id === field.id || f.fieldDef.originalId === field.id);
        }

        fieldInstances[0].setExistingContent(existingContent);
      }
    });
  }
}
